import { useMeFavouritePartner } from '@capsicum/common';
import { type PartnerWithImageMap, useFollowedPartners } from '@capsicum/socios-core';
import { useCallback, useMemo, useState } from 'react';

export type UseFollowedFilterTeamSelectorProps = {
	initialSelectedPartnersIds: Array<string>;
	partnersToShow: 'followed' | 'unfollowed';
};

const MAX_PARTNERS_ALLOWED = 140;

export const useFollowedFilterTeamSelector = ({
	initialSelectedPartnersIds,
	partnersToShow
}: UseFollowedFilterTeamSelectorProps) => {
	const { favoritePartner } = useMeFavouritePartner();
	const { followedPartners, unFollowedPartners } = useFollowedPartners(favoritePartner?.id);
	const [selectedPartners, setSelectedPartners] = useState(initialSelectedPartnersIds);

	const partners = useMemo(() => {
		const partners = partnersToShow === 'followed' ? followedPartners : unFollowedPartners;

		/** If more than MAX_PARTNERS_ALLOWED partners are selected at the same time a 414 error is thrown when submitting the endpoint. */
		while (partners.length > MAX_PARTNERS_ALLOWED) {
			const randomPartnerIndex = Math.floor(Math.random() * partners.length);
			const partnerID = partners[randomPartnerIndex]?.id;

			if (partnerID && !initialSelectedPartnersIds.includes(partnerID)) {
				partners.splice(randomPartnerIndex, 1);
			}
		}

		return partners;
	}, [partnersToShow, followedPartners, unFollowedPartners]);

	const isAllPartnersSelected = selectedPartners.length === partners.length;

	const onCheckedChange = useCallback(
		(partner: PartnerWithImageMap, checked: boolean) => {
			const id = partner.id!;

			if (selectedPartners.includes(id) && !checked) {
				setSelectedPartners(selectedPartners.filter(x => x !== id));
			} else {
				setSelectedPartners([...selectedPartners, id]);
			}
		},
		[setSelectedPartners, selectedPartners]
	);

	const onAllCheckedChange = useCallback(() => {
		if (!isAllPartnersSelected) {
			setSelectedPartners(partners.map(x => x.id!));
		} else {
			setSelectedPartners([]);
		}
	}, [isAllPartnersSelected]);

	const checkIsSelected = useCallback(
		(partnerId: PartnerWithImageMap): boolean => {
			return !!selectedPartners.find(x => x === partnerId.id);
		},
		[selectedPartners]
	);

	return {
		checkIsSelected,
		isAllPartnersSelected,
		onAllCheckedChange,
		onCheckedChange,
		/** Can be followed/unfollowed partners depending on the partnersToShow value. */
		partners,
		selectedPartners
	};
};
